import { isLocalAnalyticsEnvironment } from "@/lib/analytics/environment";
import { logger } from "@/lib/logging/logger";

/**
 * Structured identifier of a tracked UI element: `"<surface>:<element>"`.
 *
 * - `surface` — kebab-case token naming the screen/area the element lives on. It is emitted as
 *   the `surface` dimension of the generic element events, so it is derived from the id and can
 *   never drift from it. Reuse the surface token of the sibling elements on the same screen.
 * - `element` — kebab-case token naming the control within that surface, unique per surface.
 *   Name the object/action (`refresh`, `mark-paid`, `post-link`); links end in `-link`, dialogs
 *   in `-modal`, slide-out sheets in `-sheet`, confirm/cancel dialog footers in
 *   `-confirm`/`-cancel`.
 *
 * Both tokens must match the API's dimension-token rule (lowercase `a-z0-9` words joined by
 * `-`/`_`), with exactly one `:` between them — enforced at runtime by
 * {@link parseAnalyticsElementId}; a malformed id renders the element untracked (with a
 * local-environment warning).
 */
export type AnalyticsElementId = `${string}:${string}`;

/**
 * `<surface>:<element>` where each side is a lowercase token of `a-z0-9` words joined by `-`/`_`.
 * Stricter than the API's generic dimension-token rule (which allows any number of `-_:`
 * separators): exactly one colon, so the surface prefix is unambiguous.
 */
const ANALYTICS_ELEMENT_ID_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*:[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

/** Server-enforced bound on the `element_id` dimension token. */
const ANALYTICS_ELEMENT_ID_MAX_LENGTH = 128;

const isLocal = isLocalAnalyticsEnvironment();
const log = logger.child("AnalyticsElementId");

type ParsedAnalyticsElement = {
  elementId: AnalyticsElementId;
  /** The `surface` dimension — the id's prefix before the colon. */
  surface: string;
};

/**
 * Validates a structured element id and derives its `surface` dimension. Returns undefined (with
 * a local-environment warning) for a malformed id so the caller renders/behaves untracked
 * instead of emitting an event the API would drop.
 */
export function parseAnalyticsElementId(elementId: AnalyticsElementId): ParsedAnalyticsElement | undefined {
  if (elementId.length > ANALYTICS_ELEMENT_ID_MAX_LENGTH || !ANALYTICS_ELEMENT_ID_PATTERN.test(elementId)) {
    if (isLocal) {
      log.warn('Malformed analytics element id; expected "<surface>:<element>"', {
        suppressTelemetry: true,
        attributes: { elementId },
      });
    }
    return undefined;
  }

  return {
    elementId,
    surface: elementId.slice(0, elementId.indexOf(":")),
  };
}
